import type { LucideIcon } from 'lucide-react';
import {
  Bell,
  Bot,
  Building2,
  ClipboardList,
  CreditCard,
  FileQuestion,
  Handshake,
  KeyRound,
  LifeBuoy,
  Lock,
  PlugZap,
  Settings,
  Shield,
  User,
  Users,
} from 'lucide-react';

import {
  deriveSettingsScope,
  type SettingsScope,
  type SettingsScopeKind,
} from '@/lib/settingsScope';

/**
 * Single source of truth for the Admin nav group and the settings tab catalogue (ENT-3705, D3).
 *
 * `AppSidebar` renders its Admin group from {@link getAdminNavItems}; `getCommandPaletteItems`
 * maps the same items (plus one palette row per visible organisation-settings tab). `Settings.tsx`
 * and `VendorSettings.tsx` render their tab lists from {@link getVisibleOrgSettingsTabs} /
 * {@link getVisiblePersonalSettingsTabs}, so nav, palette and page never drift.
 */
export interface AdminNavContext {
  /** Admin group (Team) visibility — the sidebar's existing admin predicate. */
  isAdmin: boolean;
  /** May manage organisation settings (role `admin`/`super_admin`). Gates the org-settings item. */
  canManageOrgSettings: boolean;
  /** May manage SSO specifically; excludes virtual MSP and break-glass administrators. */
  canManageOrgSso: boolean;
  /** `custom_questionnaire_picker` organisation feature flag (ENT-6438) — Design Studio. */
  isCustomQuestionnairePickerEnabled: boolean;
  /** MSP staff acting inside a managed customer — half of the verbatim Design Studio predicate. */
  isViewingAsCustomer: boolean;
  /** Active org is the MSP's own org. */
  isMSP: boolean;
  /** MSP staff acting inside a managed customer (scope derivation). */
  isViewingManagedCustomer: boolean;
  /** Supplier portal shell. */
  isSupplierPortal: boolean;
  /** `breakglass_access` flag — Emergency access org tab. */
  isBreakglassEnabled: boolean;
  /** `developer_integrations` entitlement / PAT flag — API tokens personal tab. */
  isDeveloperIntegrationsEnabled: boolean;
  /** `enterprise_sso` entitlement — Single sign-on organisation tab. */
  isEnterpriseSsoEnabled: boolean;
  /** `supply_steward` flag targets this organisation — SupplySteward organisation tab (ENT-5511). */
  isSupplyStewardEnabled: boolean;
}

export interface AdminNavItem {
  id: 'team' | 'design-studio' | 'org-settings';
  title: string;
  url: string;
  icon: LucideIcon;
}

export interface SettingsTab {
  id: string;
  label: string;
  icon: LucideIcon;
}

interface SettingsTabDefinition extends SettingsTab {
  isVisible: (context: AdminNavContext, scopeKind: SettingsScopeKind) => boolean;
}

export function scopeFor(context: AdminNavContext): SettingsScope {
  return deriveSettingsScope({
    isMSP: context.isMSP,
    isViewingManagedCustomer: context.isViewingManagedCustomer,
    isSupplierPortal: context.isSupplierPortal,
  });
}

/** Admin nav group: Team · Design Studio (entitled) · <Scope> Settings — org settings always last. */
export function getAdminNavItems(context: AdminNavContext): AdminNavItem[] {
  const items: AdminNavItem[] = [];

  if (context.isAdmin) {
    items.push({ id: 'team', title: 'Team', url: '/team', icon: Users });
  }

  // ENT-3721 owns the correctness of this predicate — keep it VERBATIM from AppSidebar.
  if (
    context.isCustomQuestionnairePickerEnabled
    && (context.isAdmin || context.isViewingAsCustomer)
  ) {
    items.push({
      id: 'design-studio',
      title: 'Design Studio',
      url: '/questionnaires/custom',
      icon: FileQuestion,
    });
  }

  if (context.canManageOrgSettings) {
    const scope = scopeFor(context);
    items.push({ id: 'org-settings', title: scope.label, url: scope.route, icon: Settings });
  }

  return items;
}

/**
 * Organisation-settings tabs in approved order. `organization` and `security` are always present
 * (a member may read them); the rest require org-settings management. `partner` (Partner
 * information) is MSP-home only; `breakglass` (Emergency access) is flag-gated.
 */
const ORG_SETTINGS_TAB_DEFINITIONS: SettingsTabDefinition[] = [
  { id: 'organization', label: 'General & Branding', icon: Building2, isVisible: () => true },
  { id: 'security', label: 'Security', icon: Lock, isVisible: () => true },
  {
    id: 'sso',
    label: 'Single sign-on',
    icon: KeyRound,
    isVisible: (c) => c.canManageOrgSso && c.isEnterpriseSsoEnabled,
  },
  {
    id: 'certifications',
    label: 'Compliance',
    icon: Shield,
    isVisible: (c) => c.canManageOrgSettings,
  },
  { id: 'billing', label: 'Billing', icon: CreditCard, isVisible: (c) => c.canManageOrgSettings },
  {
    id: 'ai-agent',
    label: 'SupplySteward',
    icon: Bot,
    isVisible: (c) => c.canManageOrgSettings && c.isSupplyStewardEnabled,
  },
  { id: 'audit', label: 'Audit log', icon: ClipboardList, isVisible: (c) => c.canManageOrgSettings },
  {
    id: 'breakglass',
    label: 'Emergency access',
    icon: Shield,
    isVisible: (c) => c.canManageOrgSettings && c.isBreakglassEnabled,
  },
  {
    id: 'partner',
    label: 'Partner information',
    icon: Handshake,
    isVisible: (c, kind) => c.canManageOrgSettings && kind === 'msp_home',
  },
];

/** Personal-settings tabs in approved order. API tokens (integrations) is entitlement-gated. */
const PERSONAL_SETTINGS_TAB_DEFINITIONS: SettingsTabDefinition[] = [
  { id: 'profile', label: 'Profile', icon: User, isVisible: () => true },
  { id: 'security', label: 'Security', icon: Lock, isVisible: () => true },
  { id: 'support', label: 'Preferences', icon: LifeBuoy, isVisible: () => true },
  {
    id: 'integrations',
    label: 'API tokens',
    icon: PlugZap,
    isVisible: (c) => c.isDeveloperIntegrationsEnabled,
  },
];

function resolveTabs(
  definitions: SettingsTabDefinition[],
  context: AdminNavContext,
): SettingsTab[] {
  const kind = scopeFor(context).kind;
  return definitions
    .filter((tab) => tab.isVisible(context, kind))
    .map(({ isVisible: _isVisible, ...tab }) => tab);
}

export function getVisibleOrgSettingsTabs(context: AdminNavContext): SettingsTab[] {
  return resolveTabs(ORG_SETTINGS_TAB_DEFINITIONS, context);
}

export function getVisiblePersonalSettingsTabs(context: AdminNavContext): SettingsTab[] {
  return resolveTabs(PERSONAL_SETTINGS_TAB_DEFINITIONS, context);
}

// ── Supplier portal (VendorLayout / VendorSettings) ────────────────────────────────────────────
// ENT-3705: the supplier surfaces have a simpler seat model (owner/admin/member), but the same
// single-registry rule (D3) applies — the settings tabs, the sidebar Admin nav item, and the
// command-palette rows all derive from here so supplier labels/ordering/gates never require
// synchronised edits in multiple files (the round-4 missing Supplier-Security palette row was
// exactly that drift).

export type SupplierRole = 'owner' | 'admin' | 'member' | null;

export interface SupplierNavContext {
  userRole: SupplierRole;
}

interface SupplierTabDefinition extends SettingsTab {
  isVisible: (context: SupplierNavContext) => boolean;
}

function canManageSupplierOrg(context: SupplierNavContext): boolean {
  return context.userRole === 'owner' || context.userRole === 'admin';
}

/** Personal supplier tabs (any signed-in supplier member). */
const SUPPLIER_PERSONAL_TAB_DEFINITIONS: SupplierTabDefinition[] = [
  { id: 'profile', label: 'Profile', icon: User, isVisible: () => true },
  { id: 'security', label: 'Security', icon: Lock, isVisible: () => true },
  { id: 'notifications', label: 'Notifications', icon: Bell, isVisible: () => true },
];

/**
 * Supplier Settings (organisation-scoped) tabs. Org-wide supplier security (MFA enforcement +
 * access policy) is owner-only; billing is owner/admin.
 */
const SUPPLIER_ORG_TAB_DEFINITIONS: SupplierTabDefinition[] = [
  { id: 'security', label: 'Security', icon: Lock, isVisible: (c) => c.userRole === 'owner' },
  { id: 'billing', label: 'Billing', icon: CreditCard, isVisible: (c) => canManageSupplierOrg(c) },
];

function resolveSupplierTabs(
  definitions: SupplierTabDefinition[],
  context: SupplierNavContext,
): SettingsTab[] {
  return definitions
    .filter((tab) => tab.isVisible(context))
    .map(({ isVisible: _isVisible, ...tab }) => tab);
}

export function getVisibleSupplierPersonalTabs(context: SupplierNavContext): SettingsTab[] {
  return resolveSupplierTabs(SUPPLIER_PERSONAL_TAB_DEFINITIONS, context);
}

export function getVisibleSupplierOrgTabs(context: SupplierNavContext): SettingsTab[] {
  return resolveSupplierTabs(SUPPLIER_ORG_TAB_DEFINITIONS, context);
}

/** Supplier Settings admin nav item (owner/admin) for the VendorLayout Admin group. */
export function getSupplierAdminNavItem(context: SupplierNavContext): AdminNavItem | null {
  if (!canManageSupplierOrg(context)) {
    return null;
  }
  return {
    id: 'org-settings',
    title: 'Supplier Settings',
    url: '/vendor/settings/organisation',
    icon: Settings,
  };
}
